import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const initialState = {
  entryExitReasons: [],
  entryExitStatuses: [], // List of entry exit statuses from /entry-exit-statuses
  selectedEntryExitReason: null,
  activeTab: 0, // Tab state: 0 = EntryExitReasons, 1 = EntryExitStatuses
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'name',
  sortDirection: 'asc',
  filterName: '',
  filterStatusId: null, // Changed from filterIsActive to filterStatusId
  loading: false,
  error: null,
  successMessage: null,
};

const entryExitReasonsSlice = createSlice({
  name: 'entryExitReasons',
  initialState,
  reducers: {
    // Action to trigger fetch entry/exit reasons (saga will handle this)
    fetchEntryExitReasons: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchEntryExitReasonsSuccess: (state, action) => {
      state.loading = false;
      state.entryExitReasons = action.payload.data || [];
      // Extract pagination info from meta object
      state.total = action.payload.meta?.total || 0;
      state.page = action.payload.meta?.page || 1;
      state.pageSize = action.payload.meta?.size || 10;
      state.error = null;
    },
    fetchEntryExitReasonsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger add entry/exit reason
    addEntryExitReason: (state) => {
      state.loading = true;
      state.error = null;
    },
    addEntryExitReasonSuccess: (state, action) => {
      state.loading = false;
      state.entryExitReasons.push(action.payload);
      state.error = null;
      state.successMessage = i18n.t('entryExitReasons.success.created');
    },
    addEntryExitReasonFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch single entry/exit reason
    fetchEntryExitReasonById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchEntryExitReasonByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedEntryExitReason = action.payload;
      state.error = null;
    },
    fetchEntryExitReasonByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger update entry/exit reason
    updateEntryExitReason: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateEntryExitReasonSuccess: (state, action) => {
      state.loading = false;
      const index = state.entryExitReasons.findIndex((reason) => reason.id === action.payload.id);
      if (index !== -1) {
        state.entryExitReasons[index] = action.payload;
      }
      state.error = null;
      state.successMessage = i18n.t('entryExitReasons.success.updated');
    },
    updateEntryExitReasonFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger delete entry/exit reason
    deleteEntryExitReason: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteEntryExitReasonSuccess: (state, action) => {
      state.loading = false;
      state.entryExitReasons = state.entryExitReasons.filter((reason) => reason.id !== action.payload);
      state.error = null;
      state.successMessage = i18n.t('entryExitReasons.success.deleted');
    },
    deleteEntryExitReasonFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger bulk delete entry/exit reasons
    bulkDeleteEntryExitReasons: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteEntryExitReasonsSuccess: (state, action) => {
      state.loading = false;
      state.entryExitReasons = state.entryExitReasons.filter((reason) => !action.payload.includes(reason.id));
      state.error = null;
      state.successMessage = i18n.t('entryExitReasons.success.bulkDeleted', { count: action.payload.length });
    },
    bulkDeleteEntryExitReasonsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Set selected entry/exit reason
    setSelectedEntryExitReason: (state, action) => {
      state.selectedEntryExitReason = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    // Set pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page when page size changes
    },
    // Set sort
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    // Set filter
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
    setFilterStatusId: (state, action) => {
      state.filterStatusId = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
    // Fetch init data
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.entryExitStatuses = action.payload.entryExitStatuses || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Set active tab
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
  },
});

// Export actions
export const {
  fetchEntryExitReasons,
  fetchEntryExitReasonsSuccess,
  fetchEntryExitReasonsFailure,
  fetchEntryExitReasonById,
  fetchEntryExitReasonByIdSuccess,
  fetchEntryExitReasonByIdFailure,
  addEntryExitReason,
  addEntryExitReasonSuccess,
  addEntryExitReasonFailure,
  updateEntryExitReason,
  updateEntryExitReasonSuccess,
  updateEntryExitReasonFailure,
  deleteEntryExitReason,
  deleteEntryExitReasonSuccess,
  deleteEntryExitReasonFailure,
  bulkDeleteEntryExitReasons,
  bulkDeleteEntryExitReasonsSuccess,
  bulkDeleteEntryExitReasonsFailure,
  setSelectedEntryExitReason,
  clearError,
  clearSuccessMessage,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  setFilterStatusId,
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  setActiveTab,
} = entryExitReasonsSlice.actions;

// Selectors
export const selectEntryExitReasons = (state) => state.entryExitReasons.entryExitReasons;
export const selectEntryExitReasonsLoading = (state) => state.entryExitReasons.loading;
export const selectEntryExitReasonsError = (state) => state.entryExitReasons.error;
export const selectEntryExitReasonsSuccessMessage = (state) => state.entryExitReasons.successMessage;
export const selectSelectedEntryExitReason = (state) => state.entryExitReasons.selectedEntryExitReason;
export const selectEntryExitReasonsTotal = (state) => state.entryExitReasons.total;
export const selectEntryExitReasonsPage = (state) => state.entryExitReasons.page;
export const selectEntryExitReasonsPageSize = (state) => state.entryExitReasons.pageSize;
export const selectEntryExitReasonsSortColumn = (state) => state.entryExitReasons.sortColumn;
export const selectEntryExitReasonsSortDirection = (state) => state.entryExitReasons.sortDirection;
export const selectEntryExitReasonsFilterName = (state) => state.entryExitReasons.filterName;
export const selectEntryExitReasonsFilterStatusId = (state) => state.entryExitReasons.filterStatusId;
export const selectEntryExitStatuses = (state) => state.entryExitReasons.entryExitStatuses || [];
export const selectActiveTab = (state) => state.entryExitReasons.activeTab || 0;

// Export reducer
export default entryExitReasonsSlice.reducer;
